// @flow
import * as React from 'react';
import {Body, BodyTextProps} from 'sail/Text';

import {ContentFooter} from 'sail/Content';
import {AlignBox} from 'sail/Align';
import Button from 'sail/Button';
import Box from 'sail/Box';
import {InlineLink} from 'sail/InlineLink';

const messages = {
  runTest: {
    description: 'The text on the button to check for test payments',
    defaultMessage: 'Check payments',
  },
  loadingTestPayment: {
    description: 'The text when test payments are loading',
    defaultMessage: 'Looking for new test payments... ',
  },
};

type Props = {
  runCheckers: () => void,
  loadingCheckers: boolean,
  paymentCount: number,
  error: Object,
  disabled: boolean,
  dashboardUrl: string,
  integrationChecker: string,
  loginCTARedirectTarget: string,
};

const errorMessage = (error) => {
  if (error.code === '400') {
    return 'Received an error checking for test payments. Check that you are logged in and try again.';
  } else if (error.code === '500') {
    return 'Currently unable to run checks on your test payments. Please try again.';
  } else {
    return 'Error attempting to check test payments. Please try again.';
  }
};

type State = {
  hasBeenPressed: boolean,
};

class StartCheckerFooter extends React.Component<Props, State> {
  state = {
    hasBeenPressed: false,
  };

  setMetadataLink() {
    switch (this.props.integrationChecker) {
      case 'accept_a_payment':
        return (
          <React.Fragment>
            {` `} in{' '}
            <InlineLink href="#web-create-payment-intent">Step 2.</InlineLink>
          </React.Fragment>
        );
      case 'sepa_debit_accept_a_payment':
        return (
          <React.Fragment>
            {` `} in{' '}
            <InlineLink href="#web-create-payment-intent">Step 3.</InlineLink>
          </React.Fragment>
        );
      default:
        return null;
    }
  }

  handleRunCheckers() {
    this.setState({hasBeenPressed: true});
    this.props.runCheckers();
    if (
      window.siteAnalytics &&
      window.siteAnalytics.trackIntegrationCheckerAction
    ) {
      window.siteAnalytics.trackIntegrationCheckerAction(
        'run_tests_button_click',
        {},
      );
    }
  }

  render() {
    const {
      loadingCheckers,
      paymentCount,
      disabled,
      dashboardUrl,
      error,
      loginCTARedirectTarget,
      integrationChecker,
    } = this.props;
    const hasNoPayments = !loadingCheckers && paymentCount === 0;
    const foundPayments = !loadingCheckers && paymentCount;
    const {hasBeenPressed} = this.state;

    let message = null;
    if (loadingCheckers) {
      message = messages.loadingTestPayment.defaultMessage;
    } else if (error) {
      message = errorMessage(error);
    } else if (hasNoPayments) {
      message = `No test payments found. `;
    } else if (foundPayments) {
      message = `${paymentCount} test payment${
        paymentCount !== 1 ? 's' : ''
      } found. `;
    }

    return (
      <ContentFooter
        background="offset"
        start={{
          content: (
            <AlignBox
              reference={BodyTextProps}
              innerStyle={{alignItems: 'center'}}
            >
              <Button
                disabled={loadingCheckers || disabled}
                onClick={() => this.handleRunCheckers()}
                color="blue"
                icon="play"
                pending={loadingCheckers}
                label={messages.runTest.defaultMessage}
                iconPosition="left"
              />
              <Box padding={{left: 16}}>
                {hasBeenPressed ? (
                  <Body color="gray">
                    {message}
                    {hasNoPayments && hasBeenPressed && !error ? (
                      <>
                        <Body color="gray">
                          Make sure to set the metadata field{' '}
                          <code>integration_check</code> to{' '}
                          <code>{integrationChecker}</code>
                          {this.setMetadataLink()}
                        </Body>
                      </>
                    ) : null}
                  </Body>
                ) : null}
                {disabled && !hasBeenPressed ? (
                  <>
                    <Body color="gray">
                      Automatically validate your integration by logging into
                      the{' '}
                      <InlineLink
                        href={`${dashboardUrl}/login?redirect=${encodeURIComponent(
                          loginCTARedirectTarget,
                        )}`}
                      >
                        Dashboard →
                      </InlineLink>
                    </Body>
                  </>
                ) : null}
              </Box>
            </AlignBox>
          ),
        }}
      />
    );
  }
}

export default StartCheckerFooter;
